// Profile: change password, change email, cancel subscription, link to portfolio
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";

const BRAND = {
  panel: "#1a1a24",
  border: "#2a2a3a",
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  accent: "#6366f1",
  danger: "#ef4444",
  success: "#10b981",
};

function getAuthHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [emailForm, setEmailForm] = useState({ newEmail: "", password: "" });
  const [submitting, setSubmitting] = useState(""); // "password" | "email" | "cancel"

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/auth/me`, { headers: getAuthHeaders() })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      showMsg("error", "New passwords do not match.");
      return;
    }
    if (passwordForm.new.length < 8) {
      showMsg("error", "Password must be at least 8 characters.");
      return;
    }
    setSubmitting("password");
    try {
      await axios.patch(
        `${API_BASE_URL}/auth/change-password`,
        {
          current_password: passwordForm.current,
          new_password: passwordForm.new,
          new_password_confirm: passwordForm.confirm,
        },
        { headers: getAuthHeaders() }
      );
      showMsg("success", "Password updated.");
      setPasswordForm({ current: "", new: "", confirm: "" });
    } catch (err) {
      showMsg("error", err.response?.data?.detail || "Failed to update password.");
    } finally {
      setSubmitting("");
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setSubmitting("email");
    try {
      const { data } = await axios.patch(
        `${API_BASE_URL}/auth/change-email`,
        { new_email: emailForm.newEmail, password: emailForm.password },
        { headers: getAuthHeaders() }
      );
      setUser(data);
      localStorage.setItem("authUser", JSON.stringify(data));
      showMsg("success", "Email updated.");
      setEmailForm({ newEmail: "", password: "" });
    } catch (err) {
      showMsg("error", err.response?.data?.detail || "Failed to update email.");
    } finally {
      setSubmitting("");
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Cancel your paid subscription? You will keep freemium access.")) return;
    setSubmitting("cancel");
    try {
      await axios.post(`${API_BASE_URL}/auth/cancel-subscription`, {}, { headers: getAuthHeaders() });
      const u = { ...user, plan: "freemium", subscription_ends_at: null };
      setUser(u);
      localStorage.setItem("authUser", JSON.stringify(u));
      showMsg("success", "Subscription cancelled.");
    } catch (err) {
      showMsg("error", err.response?.data?.detail || "Failed to cancel.");
    } finally {
      setSubmitting("");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: BRAND.textSecondary }}>Loading profile...</div>
    );
  }
  if (!user) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <p style={{ color: BRAND.textSecondary }}>You are not signed in.</p>
        <Link to="/auth" style={{ color: BRAND.accent }}>Sign in / Sign up</Link>
      </div>
    );
  }

  const isPaid = user.plan === "monthly" || user.plan === "yearly";

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 24, fontWeight: 700, color: BRAND.text }}>Profile</h1>
      <p style={{ margin: "0 0 24px", color: BRAND.textSecondary, fontSize: 14 }}>Manage your account and subscription.</p>

      {message.text && (
        <div
          style={{
            marginBottom: 20,
            padding: 12,
            background: message.type === "success" ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
            border: `1px solid ${message.type === "success" ? BRAND.success : BRAND.danger}`,
            borderRadius: 8,
            color: message.type === "success" ? BRAND.success : BRAND.danger,
            fontSize: 14,
          }}
        >
          {message.text}
        </div>
      )}

      <section style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600, color: BRAND.text }}>Account</h2>
        <p style={{ margin: "0 0 8px", color: BRAND.textSecondary, fontSize: 14 }}>Email: <strong style={{ color: BRAND.text }}>{user.email}</strong></p>
        <p style={{ margin: 0, color: BRAND.textSecondary, fontSize: 14 }}>Plan: <strong style={{ color: BRAND.text }}>{user.plan}</strong></p>
      </section>

      <section style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600, color: BRAND.text }}>Change password</h2>
        <form onSubmit={handleChangePassword}>
          <input
            type="password"
            placeholder="Current password"
            value={passwordForm.current}
            onChange={(e) => setPasswordForm((p) => ({ ...p, current: e.target.value }))}
            required
            style={{ width: "100%", padding: "10px 14px", marginBottom: 10, background: "#111118", border: `1px solid ${BRAND.border}`, borderRadius: 8, color: BRAND.text }}
          />
          <input
            type="password"
            placeholder="New password (min 8 characters)"
            value={passwordForm.new}
            onChange={(e) => setPasswordForm((p) => ({ ...p, new: e.target.value }))}
            required
            minLength={8}
            style={{ width: "100%", padding: "10px 14px", marginBottom: 10, background: "#111118", border: `1px solid ${BRAND.border}`, borderRadius: 8, color: BRAND.text }}
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={passwordForm.confirm}
            onChange={(e) => setPasswordForm((p) => ({ ...p, confirm: e.target.value }))}
            required
            style={{ width: "100%", padding: "10px 14px", marginBottom: 12, background: "#111118", border: `1px solid ${BRAND.border}`, borderRadius: 8, color: BRAND.text }}
          />
          <button type="submit" disabled={submitting === "password"} style={{ padding: "10px 20px", background: BRAND.accent, color: BRAND.text, border: "none", borderRadius: 8, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer" }}>
            {submitting === "password" ? "Updating..." : "Update password"}
          </button>
        </form>
      </section>

      <section style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600, color: BRAND.text }}>Change email</h2>
        <form onSubmit={handleChangeEmail}>
          <input
            type="email"
            placeholder="New email"
            value={emailForm.newEmail}
            onChange={(e) => setEmailForm((p) => ({ ...p, newEmail: e.target.value }))}
            required
            style={{ width: "100%", padding: "10px 14px", marginBottom: 10, background: "#111118", border: `1px solid ${BRAND.border}`, borderRadius: 8, color: BRAND.text }}
          />
          <input
            type="password"
            placeholder="Your password"
            value={emailForm.password}
            onChange={(e) => setEmailForm((p) => ({ ...p, password: e.target.value }))}
            required
            style={{ width: "100%", padding: "10px 14px", marginBottom: 12, background: "#111118", border: `1px solid ${BRAND.border}`, borderRadius: 8, color: BRAND.text }}
          />
          <button type="submit" disabled={submitting === "email"} style={{ padding: "10px 20px", background: BRAND.accent, color: BRAND.text, border: "none", borderRadius: 8, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer" }}>
            {submitting === "email" ? "Updating..." : "Update email"}
          </button>
        </form>
      </section>

      {isPaid && (
        <section style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600, color: BRAND.text }}>Subscription</h2>
          <p style={{ margin: "0 0 12px", color: BRAND.textSecondary, fontSize: 14 }}>Cancel your paid subscription. You will keep freemium access.</p>
          <button
            onClick={handleCancelSubscription}
            disabled={submitting === "cancel"}
            style={{ padding: "10px 20px", background: "transparent", color: BRAND.danger, border: `1px solid ${BRAND.danger}`, borderRadius: 8, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer" }}
          >
            {submitting === "cancel" ? "Cancelling..." : "Cancel subscription"}
          </button>
        </section>
      )}

      <section style={{ marginTop: 24 }}>
        <Link to="/portfolio" style={{ color: BRAND.accent, fontSize: 14, textDecoration: "none" }}>
          → Open my Portfolio
        </Link>
      </section>
    </div>
  );
}
